import React, { useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import StbButton from '../security-toolbox-ui/StbButton';
import { useLabConfigurations, useLabDataCenters } from '../../hooks/lab-hooks';
import { useDcloudDatacenterTemplateTopologies } from '../../hooks/dcloud-topology-hooks';
import { useDcloudDemoContents } from '../../hooks/dcloud-hooks';
import { dcloudDemoDataCenterKey } from '../../support/dcloudService';
import { dcloudTemplateSize } from '../../support/dcloudTopologyService';
import {
  LabConfiguration,
  LabSize,
  labConfigurationOwner,
  labDataCenterCode,
  labSizeFromTemplateLabel,
  labSizeLabel,
} from '../../support/labService';
import { LabCard, LabPanelState, LabStatusTag, labErrorMessage } from './LabShared';

/** The lab columns a lookup can fill in. Everything optional: a pick is a patch. */
export type LabDcloudPick = {
  demo_uid?: number | null;
  demo_name?: string;
  /** The topology the demo itself runs on, from the demo's own record. */
  topology_uid?: string;
  /** The pre-configured template a pod is built from. A different question, so a different pick. */
  template_topology_uid?: string;
  size?: LabSize;
};

export type LabDcloudLookupPanelProps = {
  /**
   * The data centre to read. Owned by the form, not by this panel: a demo id is only meaningful
   * inside one data centre, and a second data-centre control here could be left saying London
   * while the lab says RTP.
   */
  dataCenterId?: number | null;
  /** When given, each row gets an action carrying the columns that row fills. */
  onPick?: (pick: LabDcloudPick) => void;
  /**
   * Whether the template catalog is listed as well as the demos.
   *
   * Off for the lab form, which records which demo a lab *is* and nothing about the pod it is
   * built on - that is per data centre and belongs to the publication. A "Use template" button
   * on a form with nowhere to put a template uid would be a control that does nothing.
   */
  showTemplates?: boolean;
  className?: string;
};

/**
 * Identify the dCloud lab a lab provisions from.
 *
 * A lab needs three things out of dCloud that nobody remembers: the demo id a session in that data
 * centre will report, the template topology the pod is built from, and the capacity that template
 * was published at. All three are per data centre - the same lab has a different demo id in RTP and
 * in London - so this asks dCloud for the chosen data centre's own catalog rather than showing a
 * stored list that goes stale whenever dCloud republishes a template.
 *
 * It also shows what already claims each demo in that data centre. That is the question this
 * actually gets opened for: not "does this lab exist in dCloud" but "is it already wired up, and if
 * so to what". Without it the honest answer is a guess, and the way you discover you guessed wrong
 * is the unique constraint on (scenario, data centre).
 *
 * This lives inside the lab form and nowhere else - it answers one question, asked at one moment,
 * and a standalone tab for it would be a catalog browser nobody needs. Read-only unless `onPick`
 * is given; nothing here writes to dCloud.
 */
const LabDcloudLookupPanel: React.FC<LabDcloudLookupPanelProps> = ({
  dataCenterId,
  onPick,
  showTemplates = true,
  className = '',
}) => {
  const [search, setSearch] = useState('');

  const { data: dataCenters, isLoading: dcLoading, error: dcError } = useLabDataCenters();

  const selectedDcId = dataCenterId ?? null;

  const selectedDc = useMemo(
    () => (dataCenters || []).find((entry) => entry.id === selectedDcId) || null,
    [dataCenters, selectedDcId],
  );

  // The catalog routes are allow-listed on a lowercase code, which is derived from the data
  // centre row rather than stored on it - see `labDataCenterCode`. No code, no catalog.
  const code = labDataCenterCode(selectedDc);

  /**
   * The demos, from dCloud's own content list rather than Topology Builder's inventory.
   *
   * `/api/dcloud/contents?state=saved&expand=sharedWith` is the list a session is actually
   * scheduled from, and a scheduled session reports that content's `uid` as its `parentId` -
   * which is exactly what `demo_uid` is matched on. The Topology Builder inventory answers a
   * neighbouring question about topologies and needs its own gateway token, so reading demos
   * from it meant a lab could only be identified by somebody whose TBv3 link was working.
   */
  const {
    data: demoContents,
    isLoading: demosLoading,
    error: demosError,
  } = useDcloudDemoContents(code || '', { enabled: !!code });

  const {
    data: templateCatalog,
    isLoading: templatesLoading,
    error: templatesError,
  } = useDcloudDatacenterTemplateTopologies(code || null, { enabled: !!code && showTemplates });

  // Only this data centre's publications: a demo id claimed in London says nothing about RTP.
  const { data: published } = useLabConfigurations(
    selectedDcId ? { data_center: selectedDcId } : {},
    { enabled: !!selectedDcId },
  );

  const claims = useMemo(() => {
    const byDemo = new Map<number, LabConfiguration[]>();

    (published || []).forEach((configuration) => {
      if (configuration.demo_uid === null || configuration.demo_uid === undefined) return;

      const existing = byDemo.get(configuration.demo_uid) || [];

      byDemo.set(configuration.demo_uid, [...existing, configuration]);
    });

    return byDemo;
  }, [published]);

  const needle = search.trim().toLowerCase();
  const matches = (fields: (string | number | null | undefined)[]) =>
    !needle || fields.some((field) => String(field ?? '').toLowerCase().includes(needle));

  const demos = useMemo(
    () =>
      (demoContents || [])
        .map((demo) => ({
          uid: Number(demo.uid),
          name: String(demo.name || ''),
          description: String(demo.description || ''),
          owner: String(demo.owner || ''),
          guid: String(demo.guid || ''),
          topologyUid: String(demo.topologyUid || ''),
          /**
           * The data centre the guid claims, when it differs from the one asked.
           *
           * A demo shared from elsewhere can appear in this list, and its uid is not schedulable
           * here - which surfaces as a 404 at schedule time, long after the publication looked
           * correct on this form.
           */
          foreignTo: (() => {
            const home = dcloudDemoDataCenterKey(demo);

            return home && code && home !== code ? home : null;
          })(),
          durationHours: demo.defaults?.maxDuration ?? null,
        }))
        .filter((demo) => Number.isFinite(demo.uid))
        .filter((demo) => matches([demo.uid, demo.name, demo.description, demo.owner, demo.guid]))
        .sort((left, right) => left.name.localeCompare(right.name)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [demoContents, needle, code],
  );

  const templates = useMemo(
    () =>
      (templateCatalog?.rows || [])
        .filter((template) => !!template.uid)
        .map((template) => {
          const raw = dcloudTemplateSize(template);

          return {
            uid: String(template.uid),
            name: String(template.name || ''),
            description: String(template.description || ''),
            rawSize: raw,
            // Null when dCloud's label is not one of our four. Left unmapped rather than
            // guessed, because the size decides what a pod can hold.
            size: labSizeFromTemplateLabel(raw),
          };
        })
        .filter((template) => matches([template.uid, template.name, template.description, template.rawSize]))
        .sort((left, right) => left.name.localeCompare(right.name)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [templateCatalog, needle],
  );

  const controls = (
    <TextField
      label="Search"
      size="small"
      value={search}
      onChange={(evt) => setSearch(evt.target.value)}
      helperText="Matches name, id and description."
      sx={{ minWidth: 240 }}
      disabled={!code}
    />
  );

  const unavailable = (
    <>
      {!!selectedDcId && !code && (
        <p className="m-0 text-sm text-amber-800">
          {`No dCloud code could be derived from "${
            selectedDc?.dcloud_dc_name || 'that data centre'
          }", so its catalog cannot be listed. The ids can still be entered by hand on the lab.`}
        </p>
      )}

      {/* Reported separately because they fail for different reasons. The demo list is the ordinary
          dCloud proxy; the template list is the Topology Builder gateway, which needs its own
          link. One of them being down does not mean the lab cannot be identified. */}
      {!!demosError && (
        <p className="m-0 text-sm text-amber-800">
          {`dCloud's content list could not be read (${labErrorMessage(
            demosError,
            'no dCloud link',
          )}). This is the same call the session manager makes, so it failing usually means the dCloud link under your profile needs attention.`}
        </p>
      )}

      {!!templatesError && (
        <p className="m-0 text-sm text-amber-800">
          {`Template topologies could not be read (${labErrorMessage(
            templatesError,
            'no Topology Builder link',
          )}). The demo list above is unaffected - the template uid can be entered by hand on the lab.`}
        </p>
      )}
    </>
  );

  return (
    <div className={`flex flex-col gap-4 ${className}`}>
      <LabCard
        title="dCloud lookup"
        subtitle={
          code
            ? `Read live from dCloud for ${code.toUpperCase()}. ${
                showTemplates ? 'Demo ids and templates are' : 'Demo ids are'
              } per data centre.`
            : `Pick the data centre above to read its demos${
                showTemplates ? ' and template topologies' : ''
              } from dCloud.`
        }
        actions={controls}
      >
        <LabPanelState
          loading={dcLoading}
          error={dcError}
          empty={!(dataCenters || []).length}
          emptyMessage="No data centres are recorded, so there is nothing to look up against."
          loadingLabel="Loading data centres..."
        >
          {unavailable}

          {!selectedDcId && (
            <p className="m-0 text-sm text-gray-600">
              No data centre is chosen yet. A demo id is only meaningful inside one data centre, so
              there is no catalog to show until the lab says which.
            </p>
          )}
        </LabPanelState>
      </LabCard>

      {!!code && (
        <LabCard
          title={`Demos (${demos.length})`}
          subtitle="Saved dCloud content, and content shared with you. Scheduling one produces a session that reports its uid as `parentId` - which is what a lab is matched on."
        >
          <LabPanelState
            loading={demosLoading}
            error={demosError}
            empty={!demos.length}
            emptyMessage={
              needle
                ? 'No demo in this data centre matches that search.'
                : 'dCloud lists no saved demos for this data centre. Content has to be saved before it can be scheduled, so an unsaved topology does not appear here.'
            }
            loadingLabel="Reading demos from dCloud..."
          >
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                    <th className="px-3 py-2 font-semibold">Demo id</th>
                    <th className="px-3 py-2 font-semibold">Name</th>
                    <th className="px-3 py-2 font-semibold">Topology</th>
                    <th className="px-3 py-2 font-semibold">Published here as</th>
                    {onPick && <th className="px-3 py-2 font-semibold text-right">Use</th>}
                  </tr>
                </thead>

                <tbody>
                  {demos.map((demo) => {
                    const claimed = claims.get(demo.uid) || [];

                    return (
                      <tr key={demo.uid} className="border-b border-gray-100 align-top">
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="font-mono text-xs">{demo.uid}</div>
                          {demo.guid && (
                            <div className="font-mono text-xs text-gray-500">{demo.guid}</div>
                          )}
                        </td>

                        <td className="px-3 py-2">
                          <div className="font-medium">{demo.name || '-'}</div>
                          {demo.description && (
                            <div className="text-xs text-gray-500">{demo.description}</div>
                          )}
                          <div className="text-xs text-gray-500">
                            {[
                              demo.owner ? `owner ${demo.owner}` : null,
                              demo.durationHours ? `up to ${demo.durationHours}h` : null,
                            ]
                              .filter(Boolean)
                              .join(' • ')}
                          </div>

                          {/* The one thing about a shared demo that matters here, and the one
                              thing the uid alone does not tell you. */}
                          {demo.foreignTo && (
                            <div className="text-xs text-amber-800">
                              {`This demo lives in ${demo.foreignTo.toUpperCase()}, not ${code?.toUpperCase()}. Its uid will not schedule from this publication.`}
                            </div>
                          )}
                        </td>

                        <td className="px-3 py-2 font-mono text-xs break-all">
                          {demo.topologyUid || '-'}
                        </td>

                        <td className="px-3 py-2">
                          {claimed.length ? (
                            <div className="flex flex-col gap-1">
                              {claimed.map((configuration) => (
                                <div key={configuration.id}>
                                  <div className="text-xs">{configuration.name}</div>
                                  <div className="text-xs text-gray-500">
                                    {labConfigurationOwner(configuration)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <LabStatusTag label="Not published" />
                          )}
                        </td>

                        {onPick && (
                          <td className="px-3 py-2 text-right">
                            <StbButton
                              role="default"
                              size="xsmall"
                              onClick={async () =>
                                onPick({
                                  demo_uid: demo.uid,
                                  demo_name: demo.name,
                                  // The demo's own topology, not the template it was built from.
                                  // The template is the other table, because a lab layered on a
                                  // template records both and they are rarely the same uid.
                                  ...(demo.topologyUid ? { topology_uid: demo.topologyUid } : {}),
                                })
                              }
                            >
                              Use demo
                            </StbButton>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </LabPanelState>
        </LabCard>
      )}

      {!!code && showTemplates && (
        <LabCard
          title={`Template topologies (${templates.length})`}
          subtitle="The pre-configured pod a lab is layered on top of, at the capacity dCloud published it with."
        >
          <LabPanelState
            loading={templatesLoading}
            error={templatesError}
            empty={!templates.length}
            emptyMessage={
              needle
                ? 'No template in this data centre matches that search.'
                : 'dCloud lists no template topologies for this data centre.'
            }
            loadingLabel="Reading templates from dCloud..."
          >
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                    <th className="px-3 py-2 font-semibold">Template uid</th>
                    <th className="px-3 py-2 font-semibold">Name</th>
                    <th className="px-3 py-2 font-semibold">Size</th>
                    {onPick && <th className="px-3 py-2 font-semibold text-right">Use</th>}
                  </tr>
                </thead>

                <tbody>
                  {templates.map((template) => (
                    <tr key={template.uid} className="border-b border-gray-100 align-top">
                      <td className="px-3 py-2 font-mono text-xs break-all">{template.uid}</td>

                      <td className="px-3 py-2">
                        <div className="font-medium">{template.name || '-'}</div>
                        {template.description && (
                          <div className="text-xs text-gray-500">{template.description}</div>
                        )}
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs text-gray-500">
                          {template.rawSize ? `dCloud: ${template.rawSize}` : 'dCloud states none'}
                        </div>
                        {/* Said plainly, because an unmapped size is the one case where picking
                            this template leaves the configuration's size untouched. */}
                        {template.size ? (
                          <LabStatusTag label={labSizeLabel(template.size)} sentiment="info" />
                        ) : (
                          <LabStatusTag label="No size match" sentiment="warning" />
                        )}
                      </td>

                      {onPick && (
                        <td className="px-3 py-2 text-right">
                          <StbButton
                            role="default"
                            size="xsmall"
                            onClick={async () =>
                              onPick({
                                template_topology_uid: template.uid,
                                // Omitted when dCloud's label is not one of our four, so the
                                // lab keeps the size somebody set rather than a guess.
                                ...(template.size ? { size: template.size } : {}),
                              })
                            }
                          >
                            Use template
                          </StbButton>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </LabPanelState>
        </LabCard>
      )}
    </div>
  );
};

export default LabDcloudLookupPanel;
